/**
 * BIGWA Core - Result Pattern
 * 
 * Padrão para tratamento de erros type-safe.
 * Inspirado em Rust Result<T, E> e neverthrow.
 * 
 * @example
 * // Retornando sucesso
 * return ok(data);
 * 
 * // Retornando erro
 * return err(new ValidationError({ email: 'Email inválido' }));
 * 
 * // Usando o resultado
 * const result = await createDeadline(input);
 * if (result.success) {
 *   console.log(result.data); // TypeScript sabe que é Deadline
 * } else {
 *   console.error(result.error); // TypeScript sabe que é Error
 * }
 */

// ============================================================================
// TIPOS BASE
// ============================================================================

/**
 * Resultado de sucesso
 */
export interface Ok<T> {
  readonly success: true;
  readonly data: T;
}

/**
 * Resultado de erro
 */
export interface Err<E> {
  readonly success: false;
  readonly error: E;
}

/**
 * Tipo Result - sucesso ou erro, nunca ambos
 */
export type Result<T, E = Error> = Ok<T> | Err<E>;

/**
 * Tipo AsyncResult - Promise que retorna Result
 */
export type AsyncResult<T, E = Error> = Promise<Result<T, E>>;

// ============================================================================
// CONSTRUTORES
// ============================================================================

/**
 * Cria um resultado de sucesso
 * 
 * @example
 * const result = ok({ id: '123', name: 'Test' });
 * // result.success === true
 * // result.data === { id: '123', name: 'Test' }
 */
export const ok = <T>(data: T): Ok<T> => ({
  success: true,
  data,
});

/**
 * Cria um resultado de erro
 * 
 * @example
 * const result = err(new Error('Something went wrong'));
 * // result.success === false
 * // result.error === Error
 */
export const err = <E>(error: E): Err<E> => ({
  success: false,
  error,
});

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Verifica se o resultado é sucesso
 */
export const isOk = <T, E>(result: Result<T, E>): result is Ok<T> => {
  return result.success === true;
};

/**
 * Verifica se o resultado é erro
 */
export const isErr = <T, E>(result: Result<T, E>): result is Err<E> => {
  return result.success === false;
};

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/**
 * Extrai o valor de um Result, lançando exceção se for erro
 * Use com cuidado - prefira pattern matching
 * 
 * @example
 * const data = unwrap(result); // Throws se result.success === false
 */
export const unwrap = <T, E>(result: Result<T, E>): T => {
  if (result.success) {
    return result.data;
  }
  throw result.error;
};

/**
 * Extrai o valor de um Result ou retorna valor padrão
 * 
 * @example
 * const data = unwrapOr(result, defaultValue);
 */
export const unwrapOr = <T, E>(result: Result<T, E>, defaultValue: T): T => {
  if (result.success) {
    return result.data;
  }
  return defaultValue;
};

/**
 * Mapeia o valor de sucesso para outro tipo
 * 
 * @example
 * const result: Result<number> = ok(5);
 * const mapped = map(result, n => n * 2); // ok(10)
 */
export const map = <T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => U
): Result<U, E> => {
  if (result.success) {
    return ok(fn(result.data));
  }
  return result;
};

/**
 * Mapeia o erro para outro tipo
 * 
 * @example
 * const result: Result<number, string> = err('failed');
 * const mapped = mapErr(result, e => new Error(e)); // err(Error)
 */
export const mapErr = <T, E, F>(
  result: Result<T, E>,
  fn: (error: E) => F
): Result<T, F> => {
  if (result.success) {
    return result;
  }
  return err(fn(result.error));
};

/**
 * Encadeia operações que retornam Result
 * 
 * @example
 * const result = flatMap(
 *   ok(5),
 *   n => n > 0 ? ok(n * 2) : err(new Error('must be positive'))
 * );
 */
export const flatMap = <T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => Result<U, E>
): Result<U, E> => {
  if (result.success) {
    return fn(result.data);
  }
  return result;
};

/**
 * Combina múltiplos Results em um único Result com array
 * Retorna erro no primeiro falso encontrado
 * 
 * @example
 * const results = [ok(1), ok(2), ok(3)];
 * const combined = combine(results); // ok([1, 2, 3])
 * 
 * const withError = [ok(1), err(new Error('fail')), ok(3)];
 * const combined2 = combine(withError); // err(Error)
 */
export const combine = <T, E>(results: Result<T, E>[]): Result<T[], E> => {
  const values: T[] = [];
  
  for (const result of results) {
    if (!result.success) {
      return result;
    }
    values.push(result.data);
  }
  
  return ok(values);
};

/**
 * Executa função e captura exceções em Result
 * 
 * @example
 * const result = tryCatch(() => JSON.parse(jsonString));
 */
export const tryCatch = <T>(fn: () => T): Result<T, Error> => {
  try {
    return ok(fn());
  } catch (error) {
    return err(error instanceof Error ? error : new Error(String(error)));
  }
};

/**
 * Versão async de tryCatch
 * 
 * @example
 * const result = await tryCatchAsync(() => fetch(url));
 */
export const tryCatchAsync = async <T>(
  fn: () => Promise<T>
): AsyncResult<T, Error> => {
  try {
    const data = await fn();
    return ok(data);
  } catch (error) {
    return err(error instanceof Error ? error : new Error(String(error)));
  }
};

/**
 * Match pattern - executa função baseado no tipo do resultado
 * 
 * @example
 * const message = match(result, {
 *   ok: (data) => `Success: ${data.name}`,
 *   err: (error) => `Error: ${error.message}`,
 * });
 */
export const match = <T, E, R>(
  result: Result<T, E>,
  handlers: {
    ok: (data: T) => R;
    err: (error: E) => R;
  }
): R => {
  if (result.success) {
    return handlers.ok(result.data);
  }
  return handlers.err(result.error);
};

// ============================================================================
// TIPOS AUXILIARES
// ============================================================================

/**
 * Extrai o tipo de dados de um Result
 */
export type ResultData<R> = R extends Result<infer T, unknown> ? T : never;

/**
 * Extrai o tipo de erro de um Result
 */
export type ResultError<R> = R extends Result<unknown, infer E> ? E : never;
